// InvoiceAgingReport.jsx - Outstanding invoices by aging buckets
import React, { useState, useEffect, useContext, useCallback } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { auditApi } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { toast } from "react-toastify";

const InvoiceAgingReport = () => {
  const { user } = useContext(AuthContext);

  const tenant_id = user?.tenant_id;

  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({
    customer_id: "",
    as_of_date: new Date().toISOString().split("T")[0],
  });
  const [customers, setCustomers] = useState([]);

  // Load filter options
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const customerRes = await secureAxios.get("/api/customers", {
          params: { tenant_id },
        });
        setCustomers(customerRes.data || []);
      } catch (err) {
        console.error("Error loading filter options:", err);
      }
    };
    if (tenant_id) loadFilterOptions();
  }, [tenant_id]);

  // Load report data
  const loadReport = useCallback(async () => {
    if (!tenant_id) return;
    setLoading(true);
    try {
      const response = await secureAxios.get(
        "/api/business-reports/invoice-aging",
        {
          params: {
            tenant_id,
            customer_id: filters.customer_id || undefined,
            as_of_date: filters.as_of_date || undefined,
          },
        }
      );

      if (response.data.success) {
        setReportData(response.data.data || []);
        setSummary(response.data.summary || null);

        await auditApi.logEntry({
          action: "GENERATE_INVOICE_AGING_REPORT",
          entity_type: "REPORT",
          entity_id: null,
          contains_phi: true,
          action_status: "SUCCESS",
          purpose_of_use: "OPERATIONS",
        });
      }
    } catch (err) {
      console.error("Error loading invoice aging report:", err);
      toast.error("Failed to load invoice aging report");
    } finally {
      setLoading(false);
    }
  }, [tenant_id, filters]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount || 0);
  };

  const getAgingColor = (bucket) => {
    switch (bucket) {
      case "current":
        return "success";
      case "30":
        return "info";
      case "60":
        return "warning";
      case "90":
        return "danger";
      default:
        return "dark";
    }
  };

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">
            <i className="bi bi-clock-history me-2"></i>
            Invoice Aging Report
          </h2>
          <p className="text-muted mb-0">
            Outstanding invoices by aging period
          </p>
        </div>
        <button
          className="btn btn-outline-primary"
          onClick={loadReport}
          disabled={loading}
        >
          <i className="bi bi-arrow-clockwise me-1"></i>
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Customer</label>
              <select
                className="form-select"
                value={filters.customer_id}
                onChange={(e) =>
                  setFilters({ ...filters, customer_id: e.target.value })
                }
              >
                <option value="">All Customers</option>
                {customers.map((c) => (
                  <option key={c.customer_id} value={c.customer_id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label">As of Date</label>
              <input
                type="date"
                className="form-control"
                value={filters.as_of_date}
                onChange={(e) =>
                  setFilters({ ...filters, as_of_date: e.target.value })
                }
              />
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="row g-3 mb-4">
          <div className="col-6 col-md">
            <div className="card bg-success text-white h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">Current</h6>
                <h4 className="card-title mb-0">
                  {formatCurrency(summary.current)}
                </h4>
              </div>
            </div>
          </div>
          <div className="col-6 col-md">
            <div className="card bg-info text-white h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">
                  1-30 Days
                </h6>
                <h4 className="card-title mb-0">
                  {formatCurrency(summary.days_30)}
                </h4>
              </div>
            </div>
          </div>
          <div className="col-6 col-md">
            <div className="card bg-warning text-dark h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">
                  31-60 Days
                </h6>
                <h4 className="card-title mb-0">
                  {formatCurrency(summary.days_60)}
                </h4>
              </div>
            </div>
          </div>
          <div className="col-6 col-md">
            <div className="card bg-danger text-white h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">
                  61-90 Days
                </h6>
                <h4 className="card-title mb-0">
                  {formatCurrency(summary.days_90)}
                </h4>
              </div>
            </div>
          </div>
          <div className="col-6 col-md">
            <div className="card bg-dark text-white h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">
                  90+ Days
                </h6>
                <h4 className="card-title mb-0">
                  {formatCurrency(summary.days_90_plus)}
                </h4>
              </div>
            </div>
          </div>
          <div className="col-6 col-md">
            <div className="card bg-secondary text-white h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">
                  Total Outstanding
                </h6>
                <h4 className="card-title mb-0">
                  {formatCurrency(summary.total)}
                </h4>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {/* Data Table */}
      {!loading && reportData.length > 0 && (
        <div className="card">
          <div className="table-responsive">
            <table className="table table-striped table-hover mb-0">
              <thead className="table-dark">
                <tr>
                  <th>Customer</th>
                  <th>Invoice #</th>
                  <th>Invoice Date</th>
                  <th>Due Date</th>
                  <th className="text-end">Amount</th>
                  <th className="text-end">Paid</th>
                  <th className="text-end">Balance</th>
                  <th className="text-center">Days Outstanding</th>
                  <th className="text-center">Aging Bucket</th>
                </tr>
              </thead>
              <tbody>
                {reportData.map((row, index) => (
                  <tr key={index}>
                    <td className="fw-bold">{row.customer_name}</td>
                    <td>{row.invoice_number}</td>
                    <td>
                      {row.invoice_date
                        ? new Date(row.invoice_date).toLocaleDateString()
                        : "-"}
                    </td>
                    <td>
                      {row.due_date
                        ? new Date(row.due_date).toLocaleDateString()
                        : "-"}
                    </td>
                    <td className="text-end">{formatCurrency(row.amount)}</td>
                    <td className="text-end text-success">
                      {formatCurrency(row.amount_paid)}
                    </td>
                    <td className="text-end fw-bold">
                      {formatCurrency(row.balance)}
                    </td>
                    <td className="text-center">{row.days_outstanding}</td>
                    <td className="text-center">
                      <span
                        className={`badge bg-${getAgingColor(
                          row.aging_bucket
                        )}`}
                      >
                        {row.aging_bucket === "current"
                          ? "Current"
                          : row.aging_bucket === "90_plus"
                          ? "90+"
                          : `${row.aging_bucket} Days`}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* No Data */}
      {!loading && reportData.length === 0 && (
        <div className="text-center py-5">
          <i
            className="bi bi-check-circle text-success"
            style={{ fontSize: "3rem" }}
          ></i>
          <h5 className="mt-3">No Outstanding Invoices</h5>
          <p className="text-muted">All invoices are paid in full!</p>
        </div>
      )}
    </div>
  );
};

export default InvoiceAgingReport;
